import React from "react";
import { SettingRow } from "../SettingRow";
import { RadioGroup } from "../../../../../../common/components/input/RadioGroup";
import { ConsentManagementVariant } from "../../../../../../common/service/server/dto/CMPSettingsDto";
import clsx from "clsx";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Table, Toggle, ToolTip } from "@truevault/aquilo";
import { TableDensity, TableStyle } from "@truevault/aquilo/src/components/table/Table";
import { useField, useFormState } from "react-final-form";

interface ConsentManagementVariantRowProps {
  title: string;
  helpText: React.ReactNode;
  fields: string[];
  iconPath?: string;
  recommendedValue?: string;
  allowInformationalBanner: boolean;
  allowOptOutBanner: boolean;
  allowOptInBanner: boolean;
  useOnOffPhrasing?: boolean;
  showTooltip?: boolean;
  fieldToLabel?: Record<string, string>;
  reverseOptionOrder?: boolean;
  verticalLayout?: boolean;
  fieldDescriptionToRowLabelProvider?: (
    field: string,
  ) => React.ReactNode /* provides the content of the "See States" hover for the potentially-overridable state list */;
  // Optional row-level toggle (used for US Banner master on/off)
  rowToggled?: boolean;
  onRowToggle?: (nextChecked: boolean) => void;
  // Optional content to display between the help text and the table
  contentBeforeTable?: React.ReactNode;
}

export const ConsentManagementVariantRow = ({
  title,
  helpText,
  fields,
  fieldToLabel,
  iconPath,
  recommendedValue,
  allowInformationalBanner,
  allowOptOutBanner,
  allowOptInBanner,
  useOnOffPhrasing,
  showTooltip = false,
  reverseOptionOrder = false,
  verticalLayout = false,
  fieldDescriptionToRowLabelProvider = null,
  rowToggled,
  onRowToggle,
  contentBeforeTable,
}: ConsentManagementVariantRowProps) => {
  const options = [];

  options.push({
    value: ConsentManagementVariant.NONE,
    recommended: recommendedValue == "0",
    label: <span className="d-flex justify-center">{useOnOffPhrasing ? "Off" : "None"}</span>,
  });

  if (allowInformationalBanner) {
    options.push({
      value: ConsentManagementVariant.INFORMATIONAL_ONLY,
      recommended: recommendedValue == "1",
      label: (
        <span className="d-flex align-items-center justify-center">
          {useOnOffPhrasing ? "On (Informational)" : "Informational"}
          {showTooltip && (
            <ToolTip
              text="Provides notice to visitors that their data is collected on your site. It does not allow visitors to control cookie consent. Banner text can be customized below."
              yOffset={112}
              maxWidth={"400px"}
            >
              <InformationCircleIcon
                style={{ width: "1em", height: "1em", marginLeft: "0.25em", cursor: "pointer" }}
              />
            </ToolTip>
          )}
        </span>
      ),
    });
  }

  if (allowOptOutBanner) {
    options.push({
      value: ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT,
      recommended: recommendedValue == "2",
      label: (
        <span className="d-flex align-items-center justify-center">
          {useOnOffPhrasing ? "On" : "Opt-Out"}
          {showTooltip && (
            <ToolTip
              text="Automatically opts visitors into cookie tracking and data sharing, but gives them the ability to manage and remove consent."
              yOffset={96}
              maxWidth={"400px"}
            >
              <InformationCircleIcon
                style={{ width: "1em", height: "1em", marginLeft: "0.25em", cursor: "pointer" }}
              />
            </ToolTip>
          )}
        </span>
      ),
    });
  }

  if (allowOptInBanner) {
    options.push({
      value: ConsentManagementVariant.INTERACTIVE_DEFAULT_NO_CONSENT,
      recommended: recommendedValue == "3",
      label: (
        <span className="d-flex align-items-center justify-center">
          {useOnOffPhrasing ? "On" : "Opt-In"}
          {showTooltip && (
            <ToolTip
              text="Requires visitor consent before allowing any unnecessary tracking cookies or data sharing with third parties."
              yOffset={96}
              maxWidth={"400px"}
            >
              <InformationCircleIcon
                style={{ width: "1em", height: "1em", marginLeft: "0.25em", cursor: "pointer" }}
              />
            </ToolTip>
          )}
        </span>
      ),
    });
  }

  if (reverseOptionOrder) {
    options.reverse();
  }

  let titleContent: string | React.ReactNode = title;
  if (iconPath) {
    titleContent = (
      <>
        <img
          src={iconPath}
          alt=""
          width="24"
          height="16"
          className={clsx("mr-xs")}
          style={{ verticalAlign: "middle" }}
        />
        <span style={{ verticalAlign: "middle" }}>{title}</span>
      </>
    );
  }

  // Determine if this row represents a simple on/off (binary) choice: NONE vs exactly one other option.
  const isSingleField = fields?.length === 1;
  const nonNoneOptions = options.filter((o) => o.value !== ConsentManagementVariant.NONE);
  const isBinaryChoice = isSingleField && nonNoneOptions.length === 1;

  // Single field API for the toggle use-case
  const singleFieldApi = useField(fields[0]);

  // Read current form values
  const { values } = useFormState<{ [key: string]: any }>({ subscription: { values: true } });

  // Detect missing selections
  const isValueUnset = (v: unknown) => v === undefined || v === null || v === "";

  return (
    <SettingRow
      maxWidthChildren={true}
      title={titleContent}
      helpText={helpText}
      vertical={verticalLayout}
      toggled={rowToggled}
      onToggle={onRowToggle}
    >
      <div className="max-width-896">
        {isSingleField ? (
          isBinaryChoice ? (
            // Render a toggle for simple on/off cases (e.g., Canada, EEA/UK/CH)
            (() => {
              const onVariant = nonNoneOptions[0]?.value ?? ConsentManagementVariant.NONE;
              const checked =
                singleFieldApi.input.value &&
                singleFieldApi.input.value !== ConsentManagementVariant.NONE;
              return (
                <div className="tw-boundary d-flex align-items-center" style={{ gap: "12px" }}>
                  <Toggle
                    checked={Boolean(checked)}
                    onChange={(nextChecked) =>
                      singleFieldApi.input.onChange(
                        nextChecked ? onVariant : ConsentManagementVariant.NONE,
                      )
                    }
                  />
                  {!checked && (
                    <span style={{ color: "#4338ca", fontSize: "14px", fontWeight: 400 }}>
                      ON IS RECOMMENDED
                    </span>
                  )}
                </div>
              );
            })()
          ) : (
            <RadioGroup
              field={fields[0]}
              aria-label={title}
              className="align-items-center"
              options={options}
            />
          )
        ) : rowToggled === false ? null : (
          <div className={clsx("tw-boundary max-w-[896px] overflow-x-auto")}>
            {contentBeforeTable}
            <Table
              tableStyle={TableStyle.OUTLINED}
              tableDensity={TableDensity.COMPACT}
              keyExtractor={(fieldName) => fieldName as string}
              columns={[
                {
                  key: "label",
                  header: <div>US States</div>,
                  className: "align-middle",
                  render: (fieldName: string) => {
                    const baseLabel = fieldDescriptionToRowLabelProvider
                      ? fieldDescriptionToRowLabelProvider(fieldName)
                      : fieldToLabel[fieldName];
                    const isMissing = isValueUnset(values?.[fieldName]);
                    return (
                      <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
                        <span>{baseLabel}</span>
                        {isMissing && (
                          <small style={{ color: "#DC2626" }}>Select Banner Type</small>
                        )}
                      </div>
                    );
                  },
                },
                ...options.map((option) => {
                  return {
                    key: option.value,
                    header: <div className="flex justify-center w-full">{option.label}</div>,
                    className: "text-center align-middle",
                    render: (fieldName: string) => {
                      const isMinorsField = fieldName === "usConsentManagementVariantMinors";
                      const isDisallowedForMinors =
                        option.value === ConsentManagementVariant.INFORMATIONAL_ONLY ||
                        option.value === ConsentManagementVariant.INTERACTIVE_DEFAULT_CONSENT;
                      const isDisabled = isMinorsField && isDisallowedForMinors;

                      return (
                        <div className="flex justify-center">
                          <RadioGroup
                            field={fieldName}
                            aria-label={title}
                            options={[
                              {
                                ...option,
                                hideLabel: true,
                                disabled: isDisabled,
                                disabledTooltip: isDisabled
                                  ? "Consent is required because your Business Survey responses show that you collect data from minors."
                                  : null,
                              },
                            ]}
                          />
                        </div>
                      );
                    },
                  };
                }),
              ]}
              data={fields}
            />
          </div>
        )}
      </div>
    </SettingRow>
  );
};
